/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import addProductInformationFilter from "@stoneage/App/Handlers/Documents/addProductInformationFilter";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    pagination: state.xProductInfo.data?.pagination,
});

const mapDispatchToProps = {
    addProductInformationFilter,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductInformationPaginationStyles {
    pagination?: PaginationPresentationProps;
}

const bottomPaginationStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                justify-content: flex-end;
            `,
        },
    },
};

export const paginationStyles: ProductInformationPaginationStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                justify-content: space-between;
                @media (max-width: 700px) {
                    justify-content: flex-end !important;
                }
            `,
            linkList: css`
                display: flex !important;
                @media (max-width: 700px) {
                    display: none !important;
                }
            `,
        },
    },
};

const styles = paginationStyles;

const ProductInformationPagination = ({ pagination, addProductInformationFilter, fields: { isBottom } }: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePage = (newPageIndex: number) => {
        addProductInformationFilter({ filterType: "PageNumber", value: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductInformationFilter({ filterType: "PageSize", value: newPageSize });
    };

    if (!pagination || !pagination.totalItemCount) {
        return null;
    }

    return (
        <Pagination
            {...(isBottom ? bottomPaginationStyles : styles.pagination)}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageOptions={pagination.pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="productInformation-PageSize"
            data-test-selector="productInformation_pagination"
            {...{ isBottom, pageHasMultiplePaginations: true }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductInformationPagination),
    definition: {
        group: "Product Information" as any,
        displayName: "Pagination",
        allowedContexts: ["ProductInformationPage"] as any,
        fieldDefinitions: [
            {
                name: "isBottom",
                displayName: "Pagination at the bottom?",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
